<template>
  <HeaderLeftContainer :title="$t(`domain.entities.bookmarks`)" :back-to="{ name: 'default' }">
    <v-btn icon variant="plain" @click="loadBookmarks">
      <v-icon>mdi-reload</v-icon>
      <v-tooltip activator="parent" location="start">
        {{ $t('common.actions.reload') }}
      </v-tooltip>
    </v-btn>
  </HeaderLeftContainer>

  <div class="search-box px-5 pb-3 pt-3 bodyTexts d-flex">
    <v-text-field
      v-model="search"
      class="searchField"
      :placeholder="$t('common.concepts.searchPlaceholder')"
      prepend-inner-icon="mdi-magnify"
      bg-color="rgb(var(--v-theme-surface))"
      color="rgb(var(--v-theme-lighten))"
      hide-details
      density="compact"
      variant="solo"
      clearable />
  </div>

  <v-tabs v-model="tab" class="w-100" color="primary">
    <v-tab value="global" class="w-50">{{ $t('views.bookmarks.globalBookmarks') }}</v-tab>
    <v-tab value="local" class="w-50">{{ $t('views.bookmarks.myBookmarks') }}</v-tab>
  </v-tabs>
  <v-window v-model="tab">
    <v-window-item value="global">
      <v-container v-if="loading" fluid>
        <v-row class="d-flex align-center justify-center text-center">
          <v-col class="pt-5">
            <v-progress-circular indeterminate color="primary" />
            <p>
              {{ $t('views.bookmarks.loading') }}
            </p>
          </v-col>
        </v-row>
      </v-container>
      <v-list>
        <v-list-item v-for="bookMark in globalBookmarks" :key="bookMark.id" :title="bookMark.name">
          <template #prepend>
            <v-icon :color="bookMark.color">{{ bookMark.icon }}</v-icon>
          </template>
          <template #append>
            <v-btn icon variant="flat" @click="goToBookmark(bookMark)">
              <v-icon>mdi-map-marker</v-icon>
              <v-tooltip activator="parent" location="start">{{ $t('common.actions.locate') }}</v-tooltip>
            </v-btn>

            <v-btn icon variant="flat" @click="remove(bookMark)">
              <v-icon>mdi-delete-forever</v-icon>
              <v-tooltip activator="parent" location="end">{{ $t('common.actions.delete') }}</v-tooltip>
            </v-btn>
          </template>
        </v-list-item>
      </v-list>
    </v-window-item>

    <v-window-item value="local">
      <v-container v-if="loading" fluid>
        <v-row class="d-flex align-center justify-center text-center">
          <v-col class="pt-5">
            <v-progress-circular indeterminate color="primary" />
            <p>
              {{ $t('views.bookmarks.loading') }}
            </p>
          </v-col>
        </v-row>
      </v-container>
      <v-list>
        <v-list-item v-for="bookmark in userBookmarks" :key="bookmark.id" :title="bookmark.name">
          <template #prepend>
            <v-icon :color="bookmark.color">{{ bookmark.icon }}</v-icon>
          </template>
          <template #append>
            <v-btn icon variant="flat" @click="goToBookmark(bookmark)">
              <v-icon>mdi-map-marker</v-icon>
              <v-tooltip activator="parent" location="start">{{ $t('common.actions.locate') }}</v-tooltip>
            </v-btn>

            <v-btn icon variant="flat" @click="remove(bookmark)">
              <v-icon>mdi-delete-forever</v-icon>
              <v-tooltip activator="parent" location="end">{{ $t('common.actions.delete') }}</v-tooltip>
            </v-btn>
          </template>
        </v-list-item>
      </v-list>
    </v-window-item>
  </v-window>

  <v-container v-if="!user">
    <v-row class="d-flex align-center justify-center text-center">
      <v-col class="pt-5">
        <p>
          {{ $t('messages.info.loginFirst') }}
        </p>
      </v-col>
    </v-row>
  </v-container>
  <v-btn class="left-panel-add-button" color="primary" size="large" :to="{ name: 'newbookmarks' }" icon="mdi-plus" />
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { storeToRefs } from 'pinia'

import { refDebounced, until } from '@vueuse/core'

// Components import

// Composable imports

import { useI18n } from 'vue-i18n'

import useLayers from '@tsyl/karonte/lib/composables/useLayers'
import { useNotifications } from '@tsyl/zapan2'

import HeaderLeftContainer from '@/components/HeaderLeftContainer.vue'
import bookmarksComposable from '@/composables/bookmarksComposable'
import { getBookmarks, removeBookmark } from '@/services/api/bookmarks'
import AuthStore from '@/stores/auth'
import type { Bookmark } from '@/types/Bookmark'

const { addNotification } = useNotifications()

const { t } = useI18n()

const { showBookmarks, deleteBookmark, goToBookmark } = bookmarksComposable()

const { asyncGetLayerById } = useLayers()

const auth = AuthStore()
const { user } = storeToRefs(auth)

const search = ref('' as string)
const debounced = refDebounced(search, 1000)
const tab = ref('global')
const loading = ref(true)

const allBookmarks = ref([] as Array<Bookmark>)

watch(
  () => debounced.value,
  async () => {
    await loadBookmarks()
  }
)

onMounted(async () => {
  until(user).toBeTruthy().then(loadBookmarks)
  const bookmarksLayer = await asyncGetLayerById('bookmarks')
  bookmarksLayer?.setActive(true)
})
onBeforeUnmount(async () => {
  const bookmarksLayer = await asyncGetLayerById('bookmarks')
  bookmarksLayer?.setActive(false)
})

async function loadBookmarks() {
  if (user.value) {
    try {
      allBookmarks.value = await getBookmarks({
        search: search.value ?? ''
      })
    } catch (e) {
      console.error('Get bookmarks', e)
      addNotification({
        type: 'error',
        title: t('messages.error.bookmarksLoad'),
        time: 5000
      })
    } finally {
      loading.value = false
    }
  }
}

const globalBookmarks = computed(() => {
  return allBookmarks.value.filter((b) => b.global)
})

const userBookmarks = computed(() => {
  return allBookmarks.value.filter((b) => !b.global)
})

async function remove(bookmark: Bookmark) {
  try {
    await removeBookmark(bookmark.id as string)
    deleteBookmark(bookmark)
    await loadBookmarks()
  } catch (error) {
    console.error('Delete bookmark', error)
  }
}

watch(
  [tab, globalBookmarks, userBookmarks],
  ([val, global, user]) => {
    // await loadBookmarks()
    if (val === 'global') showBookmarks(global)
    else showBookmarks(user)
  },
  { immediate: true }
)
</script>

<style scoped lang="scss">
:deep(.v-field__input) {
  padding-top: 8px !important;
}
.search-box {
  background-color: rgb(var(--v-theme-surface-light));
}
</style>
